import { type Ref } from "react";
import { css, styled } from "@pigment-css/react";
import { BaseVoiceIndicator } from "@acto/web-common/base-components";
import {
  INDICATOR_ENTER_DELAY_MS,
  INDICATOR_ENTER_MS,
  INDICATOR_LOUDNESS_MS,
  INDICATOR_EXIT_MS,
  INDICATOR_SETTLE_MS,
  REDUCED_MOTION,
} from "./motion";

const REST_HEIGHTS_PX = [12, 20, 8] as const;
const MAX_HEIGHT_PX = 20;

const Row = styled(BaseVoiceIndicator)<{ visible?: boolean }>({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "1.125rem", // No spacing token matches: three 3px bars and two 4.5px gaps.
  height: `${MAX_HEIGHT_PX}px`,
  opacity: 0,
  transform: "scale(0.8)",
  transformOrigin: "center",
  transition: `opacity ${INDICATOR_EXIT_MS}ms ease, transform ${INDICATOR_EXIT_MS}ms ease`,
  [REDUCED_MOTION]: {
    transition: "none",
  },
  variants: [
    {
      props: { visible: true },
      style: {
        opacity: 1,
        transform: "scale(1)",
        transition: `opacity ${INDICATOR_ENTER_MS}ms ease-out ${INDICATOR_ENTER_DELAY_MS}ms, transform ${INDICATOR_ENTER_MS}ms ease-out ${INDICATOR_ENTER_DELAY_MS}ms`,
      },
    },
  ],
});

const bar = css({
  flexShrink: 0,
  width: "0.1875rem",
  borderRadius: "9999px",
  // The v2 neutrals are all alpha-on-#131313 and none resolve to this over the glass fill, so the
  // bar colour stays literal.
  background: "#505050",
  // Linear, not eased: the loudness signal already arrives throttled, and easing between samples
  // reads as lag.
  transition: `height ${INDICATOR_LOUDNESS_MS}ms linear`,
  [REDUCED_MOTION]: {
    transition: "none",
  },
});

const barSettling = css({
  transition: `height ${INDICATOR_SETTLE_MS}ms ease-out`,
});

interface VoiceIndicatorProps {
  ref?: Ref<HTMLDivElement>;
  visible: boolean;
  settling: boolean;
}

export function VoiceIndicator({ ref, visible, settling }: VoiceIndicatorProps) {
  return (
    <Row
      ref={ref}
      visible={visible}
      aria-hidden="true"
      restHeightsPx={REST_HEIGHTS_PX}
      maxHeightPx={MAX_HEIGHT_PX}
      slots={{ bar: settling ? `${bar} ${barSettling}` : bar }}
    />
  );
}
